import React from "react";
import { Button } from "../../atoms/button/button";
import { Text, plainText, type TextLevel } from "../../atoms/text/text";
import { LINK_POSITION_BODY } from "../../services/analytics";
import { ctaAriaLabel } from "../../services/cta-aria-label";
import { isSafeHref } from "../../services/safe-href";
import { isSafeImageSrc } from "../../services/safe-image-src";
import type {
  LineupColumn,
  SignatureGridProps as SignatureGridContract
} from "./channel-lineup.types";
import { ChannelRow } from "./channel-row";
import { GridSection } from "./grid-section";
import { IndicatorLegend } from "./indicator-legend";
import type { LineupRowCopy } from "./row-copy.shared";
import type { LineupGridSection } from "./signature-grid.shared";
import { SubscriptionRow } from "./subscription-row";
import { TooltipScope } from "./tooltip-scope.client";
import styles from "./signature-grid.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/* Bails out on a re-sort: the pooled `row` and the grid-level props are the
   same objects, so only the row order changes. */
const MemoChannelRow = React.memo(ChannelRow);

/**
 * The Signature packages grid's sections — GS1-GS13. It places the sections it is handed, in the
 * order it is handed them, and draws the two things a section cannot know about itself: which one
 * carries GS9's ✓/$/★ key, and which one is MyFree and therefore gets GS11's lockup above its rows.
 */

/**
 * Extends `channel-lineup.types.ts`'s `SignatureGridProps` rather than redeclaring it, so the two
 * cannot drift and a caller holding the closed contract type is assignable here.
 */
export interface SignatureGridProps extends SignatureGridContract {
  /**
   * Resolver-fed (camelCase). The grid's columns in authored order; every row's `cells` is
   * positional against this array. Nothing here re-orders or drops one.
   */
  columns: LineupColumn[];
  /**
   * Resolver-fed (camelCase). Already bucketed, ordered and defaulted — GS1/GS3's order, GS6's
   * emptiness and GS7's `defaultOpen` are all decided before they get here, by
   * `buildSignatureSections()`.
   */
  sections: LineupGridSection[];
  /** GR5 — the legal note for an RSN's tier, keyed by tier number. */
  tierLegalNotes?: Record<number, string>;
  /** GR5 — the legal note an RSN falls back to when its tier has no entry in `tierLegalNotes`. */
  rsn_legal_default_content?: string;
  /**
   * AX6 — makes every row's channel logo a tooltip trigger showing the channel name. Off by
   * default; `channel-identity.tsx` carries the accessibility argument for that default, and
   * design-conflicts records it.
   */
  show_channel_logo_tooltip?: boolean;
  /** GS11 — the MyFree lockup above that section's rows. Absent, no logo. */
  myfree_logo_url?: string;
  /** GS11's button wording. Needs a URL too, or nothing renders. */
  myfree_cta_label?: string;
  /** GS11's button destination. Needs a label too, or nothing renders. */
  myfree_cta_url?: string;
  /** GS11's button accessible name, as authored. Absent, the visible wording names it. */
  myfree_cta_aria_label?: string;
  /** GS9's ✓ line, content-managed. */
  legend_included_content?: string;
  /** GS9's $ line, content-managed. */
  legend_addon_content?: string;
  /** GS9's ★ line, content-managed. */
  legend_promo_content?: string;
  /** GS6 — the wording when there is nothing to show. */
  empty_message_content?: string;
  /** Page-set. The authored wording every row draws (`row-copy.shared.ts`). */
  rowCopy?: LineupRowCopy;
  /**
   * AX5 — the level section names take, and the same level `signature-grid.client.tsx` forwards to
   * `SearchResults.headingLevel` for the "Search results" heading: one authored level orders both
   * modes, so a second, independently authored level could put them out of order with neither
   * component able to detect it.
   */
  sectionHeadingLevel?: TextLevel;
  /**
   * GS8 — pixels of pinned grid header above the sections. An override, and the page sets none:
   * `sticky-offset.client.tsx` publishes the measured header height as `--cl-section-offset` on the
   * scope around this view, and every section inherits it.
   */
  stickyOffset?: number;
  /** Layout only. */
  className?: string;
}

/** GS11 — "an open MyFree section shows the MyFree logo above its rows. */
export interface MyFreeIntroProps {
  /** GS11's lockup. Absent, or not a safe image URL, draws no logo. */
  logoUrl?: string;
  /** GS11's button wording. Needs a URL too, or nothing renders. */
  ctaLabel?: string;
  /** GS11's button destination. Needs a label too, or nothing renders. */
  ctaHref?: string;
  /** GS11's button accessible name, as authored. Absent, the visible wording names it. */
  ctaAriaLabel?: string;
}

/** Exported for `search-results.tsx`: GQ4 puts the same lockup under the
 *  MyFree label in the merged list, where there is no section to carry it. */
function MyFreeIntroImpl({ logoUrl, ctaLabel, ctaHref, ctaAriaLabel: askedAriaLabel }: MyFreeIntroProps) {
  const logo = (logoUrl ?? "").trim();
  const label = (ctaLabel ?? "").trim();
  const safeLogo = isSafeImageSrc(logo) ? logo : "";
  // GS11 — both a label and a link are required, or no button renders.
  /* Trimmed, like the label above it, but not for the gate's sake: `isSafeHref` refuses a
     whitespace-only value on its own (WAT-12420 runs its dead-link test on `href.trim()`). */
  const href = (ctaHref ?? "").trim();
  const hasCta = Boolean(label) && isSafeHref(href);

  if (!safeLogo && !hasCta) {
    return null;
  }

  return (
    <div className={styles.myFree}>
      {safeLogo !== "" && (
        <img alt="" className={styles.myFreeLogo} decoding="async" loading="lazy" src={safeLogo} />
      )}
      {hasCta && (
        <Button
          // An <a>, not a <button>: Button renders an anchor whenever `href` is set, so GTM's own
          // Link Click trigger reports it and nothing here fires a second event.
          ariaLabel={ctaAriaLabel(askedAriaLabel)}
          href={href}
          linkAttributes
          linkPosition={LINK_POSITION_BODY}
          size="small"
          variant="secondary"
        >
          {label}
        </Button>
      )}
    </div>
  );
}
export const MyFreeIntro = withSafeRender(MyFreeIntroImpl, "MyFreeIntro");

function SignatureGridImpl({
  columns,
  sections,
  tierLegalNotes,
  rsn_legal_default_content: rsnLegalDefault,
  show_channel_logo_tooltip: showChannelLogoTooltipField = false,
  myfree_logo_url: myFreeLogoUrl,
  myfree_cta_label: myFreeCtaLabel,
  myfree_cta_url: myFreeCtaUrl,
  myfree_cta_aria_label: myFreeCtaAriaLabel,
  legend_included_content: legendIncluded,
  legend_addon_content: legendAddon,
  legend_promo_content: legendPromo,
  empty_message_content: emptyMessageContent,
  rowCopy,
  sectionHeadingLevel = "h3",
  stickyOffset,
  className
}: SignatureGridProps) {
  /* AX6 — `=== true`, not a truthiness test. */
  const showChannelLogoTooltip = showChannelLogoTooltipField === true;

  /* The track count, published to the subtree so a row does not re-derive
     it. `repeat(0, …)` is invalid CSS and takes the whole declaration with
     it, so the floor is 1 — the same guard `grid-header.tsx` carries. */
  const trackStyle = { "--cl-columns": Math.max(columns.length, 1) } as React.CSSProperties;

  /* GS6 — "if there are no subscriptions and no channels, the grid shows the same message as GQ5
     instead of sections". */
  if (sections.length === 0) {
    const message = plainText((emptyMessageContent ?? "").trim());
    return (
      <div className={[styles.grid, className].filter(Boolean).join(" ")} style={trackStyle}>
        {message !== "" && (
          <Text as="p" className={styles.empty} variant="body-2">
            {message}
          </Text>
        )}
      </div>
    );
  }

  return (
    /* One tooltip owner for every badge and logo in the grid (AX6). */
    <TooltipScope>
      <div className={[styles.grid, className].filter(Boolean).join(" ")} style={trackStyle}>
        {sections.map(section => {
          const subscriptionRows = section.subscriptionRows ?? [];
          /* GS9 and GS11 key off the section kind, never its wording: GS1's MyFree label is
             content-managed and GS3 renames the add-on one, so a heading match would drop the
             legend and the lockup silently the first time CI changed a word. */
          const isMyFree = section.kind === "myFree";
          /* GS9 narrowed by GQ3's rule — the key lists only what this section's rows draw. A Category
             bucket carries it too once its rows draw a $ or ★ (`16575:26486`). */
          const memberships = section.memberships ?? [];
          const showLegend =
            (section.kind === "addon" && memberships.length !== 0) ||
            (section.kind === "category" && memberships.some(membership => membership !== "included"));

          return (
            <GridSection
              className={styles.section}
              defaultOpen={section.defaultOpen}
              heading={section.heading}
              headingLevel={sectionHeadingLevel}
              introSlot={
                isMyFree ? (
                  <MyFreeIntro
                    ctaAriaLabel={myFreeCtaAriaLabel}
                    ctaHref={myFreeCtaUrl}
                    ctaLabel={myFreeCtaLabel}
                    logoUrl={myFreeLogoUrl}
                  />
                ) : undefined
              }
              /* Kind and heading, never the heading alone. */
              key={`${section.kind ?? "section"}:${section.heading}`}
              legendSlot={
                showLegend ? (
                  /* GS9 — the shared leaf whole; which section carries it is this component's only
                     business. */
                  <IndicatorLegend
                    addonLine={legendAddon}
                    includedLine={legendIncluded}
                    memberships={section.memberships}
                    promoLine={legendPromo}
                  />
                ) : undefined
              }
              stickyOffset={stickyOffset}
            >
              {subscriptionRows.map(row => (
                <SubscriptionRow columns={columns} key={row.subscription.id} row={row} rowCopy={rowCopy} />
              ))}

              {/* GS4 — in the Category view the add-on section groups its rows
                  under a labelled strip per category, in category order. Every
                  other section, and every other sort, is the flat list. */}
              {section.groups ?
                section.groups.map(group => (
                  /* Unique because `addonGroups` is the only producer of
                     `groups` and it maps the deduped authored categories. */
                  <div className={styles.group} key={group.label}>
                    <Text as="p" className={styles.groupLabel} variant="label">
                      {plainText(group.label)}
                    </Text>
                    {group.rows.map(row => (
                      <MemoChannelRow
                        columns={columns}
                        defaultLegalNote={rsnLegalDefault}
                        key={row.channel.id}
                        row={row}
                        rowCopy={rowCopy}
                        showLogoTooltip={showChannelLogoTooltip}
                        tierLegalNotes={tierLegalNotes}
                      />
                    ))}
                  </div>
                )) :
                section.rows.map(row => (
                  <MemoChannelRow
                    columns={columns}
                    defaultLegalNote={rsnLegalDefault}
                    key={row.channel.id}
                    row={row}
                    rowCopy={rowCopy}
                    showLogoTooltip={showChannelLogoTooltip}
                    tierLegalNotes={tierLegalNotes}
                  />
                ))}
            </GridSection>
          );
        })}
      </div>
    </TooltipScope>
  );
}
export const SignatureGrid = withSafeRender(SignatureGridImpl, "SignatureGrid");

export default SignatureGrid;
